/**
 * Retake Course API Service
 *
 * Centralized service for all retake course API calls.
 * Handles authentication, error responses, and data transformation.
 *
 * @version 2.0.0
 */

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

// ===== Constants =====

const API_BASE_PATH = '/api/retake-course/v1';

/**
 * Error types for handling different API errors
 */
export const ErrorTypes = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  BAD_REQUEST: 'BAD_REQUEST',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN',
};

/**
 * Hebrew error messages for user display
 */
const ERROR_MESSAGES = {
  [ErrorTypes.UNAUTHORIZED]: 'יש להתחבר מחדש כדי להמשיך',
  [ErrorTypes.FORBIDDEN]: 'אין לך הרשאה לבצע פעולה זו',
  [ErrorTypes.NOT_FOUND]: 'המשאב המבוקש לא נמצא',
  [ErrorTypes.BAD_REQUEST]: 'הבקשה אינה תקינה',
  [ErrorTypes.SERVER_ERROR]: 'שגיאת שרת. נסה שוב מאוחר יותר',
  [ErrorTypes.NETWORK_ERROR]: 'שגיאת רשת. בדוק את החיבור לאינטרנט',
  [ErrorTypes.UNKNOWN]: 'אירעה שגיאה לא צפויה',
};

// ===== Custom Error Class =====

/**
 * Custom API Error class with additional context
 */
export class RetakeApiError extends Error {
  /**
   * @param {string} message - Error message
   * @param {string} type - Error type from ErrorTypes
   * @param {number} statusCode - HTTP status code
   * @param {Object} data - Additional error data from API
   */
  constructor(message, type, statusCode = null, data = null) {
    super(message);
    this.name = 'RetakeApiError';
    this.type = type;
    this.statusCode = statusCode;
    this.data = data;
    this.userMessage = ERROR_MESSAGES[type] || message;
  }
}

// ===== Helper Functions =====

/**
 * Get the base URL for API calls
 * @returns {string} Base URL
 */
const getBaseUrl = () => {
  const config = getConfig();
  return config.LMS_BASE_URL || '';
};

/**
 * Build full API URL
 * @param {string} endpoint - API endpoint path
 * @returns {string} Full URL
 */
const buildUrl = (endpoint) => `${getBaseUrl()}${API_BASE_PATH}${endpoint}`;

/**
 * Determine error type from HTTP status code
 * @param {number} statusCode - HTTP status code
 * @returns {string} Error type
 */
const getErrorTypeFromStatus = (statusCode) => {
  switch (statusCode) {
    case 400:
      return ErrorTypes.BAD_REQUEST;
    case 401:
      return ErrorTypes.UNAUTHORIZED;
    case 403:
      return ErrorTypes.FORBIDDEN;
    case 404:
      return ErrorTypes.NOT_FOUND;
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorTypes.SERVER_ERROR;
    default:
      return ErrorTypes.UNKNOWN;
  }
};

/**
 * Handle API error response and throw appropriate error
 * @param {Error} error - Axios error object
 * @throws {RetakeApiError} Formatted API error
 */
const handleApiError = (error) => {
  // Network error (no response)
  if (!error.response) {
    throw new RetakeApiError(
      error.message || 'Network error',
      ErrorTypes.NETWORK_ERROR,
      null,
      null,
    );
  }

  const { status, data } = error.response;
  const errorType = getErrorTypeFromStatus(status);
  const serverMessage = data?.message || data?.error || error.message;

  throw new RetakeApiError(
    serverMessage,
    errorType,
    status,
    data,
  );
};

/**
 * Execute API request with error handling
 * @param {Function} requestFn - Async function that makes the request
 * @returns {Promise<any>} Response data
 * @throws {RetakeApiError} On any error
 */
const executeRequest = async (requestFn) => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    // If already a RetakeApiError, re-throw
    if (error instanceof RetakeApiError) {
      throw error;
    }
    // Otherwise, handle and transform - this always throws
    handleApiError(error);
    // This return is unreachable but satisfies ESLint consistent-return
    return undefined;
  }
};

// ===== API Methods =====

/**
 * Fetch eligibility data for a course
 *
 * @param {string} courseId - Course identifier
 * @returns {Promise<Object>} Eligibility data
 * @throws {RetakeApiError} On API error
 *
 * @example
 * const eligibility = await fetchEligibility('course-v1:ORG+NUM+RUN');
 * console.log(eligibility.is_eligible);
 */
export const fetchEligibility = async (courseId) => {
  if (!courseId) {
    throw new RetakeApiError(
      'Course ID is required',
      ErrorTypes.BAD_REQUEST,
      400,
    );
  }

  return executeRequest(async () => {
    const client = getAuthenticatedHttpClient();
    const url = buildUrl(`/courses/${courseId}/eligibility/`);
    return client.get(url);
  });
};

/**
 * Fetch course chapters for partial reset selection
 *
 * @param {string} courseId - Course identifier
 * @returns {Promise<Object>} Chapters data with structure:
 *   { status, chapters: [{ id, display_name, subsections_count, has_graded_content }] }
 * @throws {RetakeApiError} On API error
 *
 * @example
 * const { chapters } = await fetchCourseChapters('course-v1:ORG+NUM+RUN');
 */
export const fetchCourseChapters = async (courseId) => {
  if (!courseId) {
    throw new RetakeApiError(
      'Course ID is required',
      ErrorTypes.BAD_REQUEST,
      400,
    );
  }

  return executeRequest(async () => {
    const client = getAuthenticatedHttpClient();
    const url = buildUrl(`/courses/${courseId}/chapters/`);
    return client.get(url);
  });
};

/**
 * Initiate course reset (full or partial)
 *
 * @param {string} courseId - Course identifier
 * @param {string} resetType - 'full' or 'partial'
 * @param {string[]} chapterIds - Array of chapter IDs (required for partial reset)
 * @returns {Promise<Object>} Reset result with structure:
 *   { status, message, retake_id, summary }
 * @throws {RetakeApiError} On API error or validation failure
 *
 * @example
 * // Full reset
 * const result = await initiateReset('course-v1:ORG+NUM+RUN', 'full');
 *
 * // Partial reset
 * const result = await initiateReset('course-v1:ORG+NUM+RUN', 'partial', ['chapter-id-1']);
 */
export const initiateReset = async (courseId, resetType, chapterIds = []) => {
  // Validate inputs
  if (!courseId) {
    throw new RetakeApiError(
      'Course ID is required',
      ErrorTypes.BAD_REQUEST,
      400,
    );
  }

  if (!resetType || !['full', 'partial'].includes(resetType)) {
    throw new RetakeApiError(
      'Invalid reset type. Must be "full" or "partial"',
      ErrorTypes.BAD_REQUEST,
      400,
    );
  }

  if (resetType === 'partial') {
    if (!Array.isArray(chapterIds) || chapterIds.length === 0) {
      throw new RetakeApiError(
        'At least one chapter must be selected for partial reset',
        ErrorTypes.BAD_REQUEST,
        400,
      );
    }

    // Validate each chapter ID is a non-empty string
    const invalidChapter = chapterIds.find(
      (id) => typeof id !== 'string' || !id.trim(),
    );
    if (invalidChapter !== undefined) {
      throw new RetakeApiError(
        'Invalid chapter ID format',
        ErrorTypes.BAD_REQUEST,
        400,
      );
    }
  }

  // Build request payload
  const payload = {
    reset_type: resetType,
  };

  if (resetType === 'partial') {
    payload.chapter_ids = chapterIds;
  }

  return executeRequest(async () => {
    const client = getAuthenticatedHttpClient();
    const url = buildUrl(`/courses/${courseId}/reset/`);
    return client.post(url, payload);
  });
};

/**
 * Fetch reset history for a course
 *
 * @param {string} courseId - Course identifier
 * @returns {Promise<Object>} History data with structure:
 *   { status, history: [{ id, reset_date, reset_type, status, ... }], total_resets }
 * @throws {RetakeApiError} On API error
 *
 * @example
 * const { history, total_resets } = await fetchResetHistory('course-v1:ORG+NUM+RUN');
 */
export const fetchResetHistory = async (courseId) => {
  if (!courseId) {
    throw new RetakeApiError(
      'Course ID is required',
      ErrorTypes.BAD_REQUEST,
      400,
    );
  }

  return executeRequest(async () => {
    const client = getAuthenticatedHttpClient();
    const url = buildUrl(`/courses/${courseId}/history/`);
    return client.get(url);
  });
};

// ===== Response Transformers =====

/**
 * Transform eligibility API response to frontend-friendly format
 *
 * @param {Object} data - Raw API response
 * @returns {Object} Transformed eligibility data
 */
export const transformEligibilityResponse = (data) => ({
  isEligible: data.is_eligible,
  retakeEnabled: data.retake_enabled,
  hasCompleted: data.has_completed,
  cooldownPassed: data.cooldown_passed,
  nextAvailableDate: data.next_available_date,
  allowPartialReset: data.allow_partial_reset,
  chapters: data.chapters || [],
  maxRetakes: data.max_retakes,
  currentRetakeCount: data.current_retake_count,
  resetsRemaining: data.resets_remaining,
  message: data.message,
  hasCertificate: data.has_certificate,
  certificateUrl: data.certificate_url,
});

/**
 * Transform chapter API response to frontend-friendly format
 *
 * @param {Object} data - Raw API response
 * @returns {Object[]} Array of transformed chapter objects
 */
export const transformChaptersResponse = (data) => (data.chapters || []).map((chapter) => ({
  id: chapter.id,
  displayName: chapter.display_name,
  position: chapter.position,
  subsectionsCount: chapter.subsections_count,
  unitsCount: chapter.units_count,
  hasGradedContent: chapter.has_graded_content,
}));

// ===== Default Export =====

const retakeService = {
  fetchEligibility,
  fetchCourseChapters,
  initiateReset,
  fetchResetHistory,
  transformEligibilityResponse,
  transformChaptersResponse,
  ErrorTypes,
  RetakeApiError,
};

export default retakeService;
